/**
 * GYM-1274 — Member Account & Privacy page.
 *
 * Two sections:
 *   1. Export my data  — request export, show progress/download/countdown.
 *   2. Delete my data  — two-step friction modal + grace banner.
 *
 * Bilingual (EN/AR + RTL). Fully accessible (role=status, role=alert,
 * labelled inputs, focus management).
 */

import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Download, Loader2, AlertTriangle, CheckCircle, Clock, FileDown, MapPin } from "lucide-react";

import {
  memberPrivacyStatus,
  memberRequestExport,
  memberCancelErasure,
  type PrivacyStatus,
} from "@/lib/api/member";
import { useCurrentMember } from "@/pages/member/ui/useCurrentMember";
import { MemberPage, Notice, PageHeader, Reveal, SectionCard, SkeletonBlock, Surface } from "./ui/kit";
import { getInitials, pillButtonClass } from "./ui/styles";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatLocalDate(iso: string, locale: string): string {
  try {
    return new Date(iso).toLocaleDateString(locale, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return iso.split("T")[0];
  }
}

function HoursCountdown({ hours }: { hours: number }) {
  const { i18n } = useTranslation();
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  const numeral = (n: number) =>
    i18n.language === "ar"
      ? n.toLocaleString("ar-EG")
      : String(n);
  return <span>{numeral(h)}h {numeral(m)}m</span>;
}

// ---------------------------------------------------------------------------
// Export card
// ---------------------------------------------------------------------------

function ExportCard() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data: privacy, isLoading } = useQuery({
    queryKey: ["member-privacy"],
    queryFn: memberPrivacyStatus,
    refetchInterval: (query) => {
      const d = query.state.data as PrivacyStatus | undefined;
      const s = d?.latest_export?.status;
      return s === "pending" || s === "running" ? 5000 : false;
    },
  });

  const export_ = privacy?.latest_export;

  const requestMutation = useMutation({
    mutationFn: memberRequestExport,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["member-privacy"] }),
  });

  if (isLoading) {
    return (
      <Surface className="space-y-3">
        <SkeletonBlock className="h-5 w-40" />
        <SkeletonBlock className="h-4 w-full" />
        <SkeletonBlock className="h-11 w-44 rounded-full" />
      </Surface>
    );
  }

  const canRequest =
    !export_ ||
    export_.status === "expired" ||
    export_.status === "failed" ||
    export_.status === "denied";

  return (
    <SectionCard icon={FileDown} title={t("memberPortal.privacy.exportTitle")} subtitle={t("memberPortal.privacy.exportDesc")}>
      <div className="space-y-4">
        {export_ && (export_.status === "pending" || export_.status === "running") && (
          <div role="status" className="space-y-2">
            <p className="flex items-center gap-2 text-sm font-medium text-ink-900">
              <Loader2 className="h-4 w-4 animate-spin text-primary-600" aria-hidden="true" />
              {export_.status === "pending" ? t("memberPortal.privacy.exportPending") : t("memberPortal.privacy.exportRunning")}
            </p>
            <div className="h-2 w-full overflow-hidden rounded-full bg-ink-900/[0.06] dark:bg-white/[0.08]">
              <div className={`h-full rounded-full bg-primary-500 animate-pulse ${export_.status === "pending" ? "w-1/3" : "w-2/3"}`} />
            </div>
          </div>
        )}

        {export_ && export_.status === "pending_review" && (
          <Notice tone="warning" icon={Clock}>{t("memberPortal.privacy.exportPendingReview")}</Notice>
        )}

        {export_ && export_.status === "ready" && export_.download_url && (
          <div className="space-y-3">
            <Notice tone="success" icon={CheckCircle}>
              <span role="status">{t("memberPortal.privacy.exportReady")}</span>
              {export_.hours_until_expiry !== null && (
                <span className="mt-0.5 block text-xs text-ink-500">
                  {t("memberPortal.privacy.exportExpiresIn")}{" "}
                  <HoursCountdown hours={export_.hours_until_expiry} />
                </span>
              )}
            </Notice>
            <a href={export_.download_url + `?token=${encodeURIComponent("")}`} download className={pillButtonClass("primary", "md")}>
              <Download className="h-4 w-4" aria-hidden="true" />
              {t("memberPortal.privacy.downloadJson")}
            </a>
          </div>
        )}

        {export_ && export_.status === "expired" && (
          <p className="text-sm text-ink-500">{t("memberPortal.privacy.exportExpired")}</p>
        )}

        {export_ && export_.status === "denied" && (
          <Notice tone="danger" icon={AlertTriangle}>
            {t("memberPortal.privacy.exportDenied")}
            {export_.denial_reason && `: ${export_.denial_reason}`}
          </Notice>
        )}

        {canRequest && (
          <button
            type="button"
            onClick={() => requestMutation.mutate()}
            disabled={requestMutation.isPending}
            className={pillButtonClass("primary", "md")}
          >
            {requestMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <FileDown className="h-4 w-4" aria-hidden="true" />}
            {t("memberPortal.privacy.requestExport")}
          </button>
        )}
      </div>
    </SectionCard>
  );
}


// ---------------------------------------------------------------------------
// Grace banner (rendered by MemberPortalLayout above <Outlet/>)
// ---------------------------------------------------------------------------

export function ErasureGraceBanner() {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();

  const { data: privacy } = useQuery({
    queryKey: ["member-privacy"],
    queryFn: memberPrivacyStatus,
  });

  const cancelMutation = useMutation({
    mutationFn: (id: string) => memberCancelErasure(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["member-privacy"] }),
  });

  const activeErasure = privacy?.active_erasure;
  if (!activeErasure || activeErasure.status !== "scheduled") return null;

  return (
    <div role="status" className="flex flex-col gap-3 rounded-2xl bg-warning-500/10 px-4 py-3 text-sm sm:flex-row sm:items-center sm:justify-between">
      <span className="flex items-start gap-3 text-ink-900">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning-500" aria-hidden="true" />
        {t("memberPortal.privacy.graceBanner", {
          date: formatLocalDate(activeErasure.grace_deadline, i18n.language),
        })}
      </span>
      <button
        type="button"
        className={pillButtonClass("secondary", "sm")}
        onClick={() => cancelMutation.mutate(activeErasure.id)}
        disabled={cancelMutation.isPending}
      >
        {cancelMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : null}
        {t("memberPortal.privacy.cancelErasure")}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Post-erasure neutral profile guard
// ---------------------------------------------------------------------------

export function ErasureNeutralGuard({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const { data: privacy } = useQuery({
    queryKey: ["member-privacy"],
    queryFn: memberPrivacyStatus,
  });

  if (privacy?.erasure_status) {
    return (
      <div className="py-12 text-center text-ink-500">
        <AlertTriangle className="h-8 w-8 mx-auto mb-3 text-ink-300" />
        <p>{t("memberPortal.privacy.profileRemoved")}</p>
      </div>
    );
  }
  return <>{children}</>;
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function MemberPrivacyPage() {
  const { t } = useTranslation();
  const member = useCurrentMember();

  return (
    <MemberPage>
      <PageHeader title={t("memberPortal.privacy.pageTitle")} subtitle={t("memberUi.account.privacySubtitle", "Your profile and the data we hold about you")} />

      {member && (
        <Reveal>
          <Surface className="flex items-center gap-4">
            <span className="inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-primary-500/10 text-lg font-semibold text-primary-700 dark:text-primary-300">
              {getInitials(member.full_name_en)}
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-base font-semibold text-ink-900">{member.full_name_en}</p>
              <p className="truncate text-sm text-ink-500">{member.email}</p>
              <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-ink-500">
                <span className="tabular-nums tracking-wide">{member.member_code}</span>
                {member.home_branch_name && (
                  <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" aria-hidden="true" />{member.home_branch_name}</span>
                )}
              </p>
            </div>
          </Surface>
        </Reveal>
      )}

      <Reveal>
        <ExportCard />
      </Reveal>
    </MemberPage>
  );
}
